import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { messagesApi } from '@/api/messages';
import { authApi } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useSocketEvents } from '@/hooks/useSocket';
import MessageList from '@/components/chat/MessageList';
import MessageInput from '@/components/chat/MessageInput';
import { Lock } from 'lucide-react';
import { connectSocket } from '@/lib/socket';
import { Message } from '@/types';

/**
 * Минимальный chat-only embed для русnt.ru iframe-интеграции.
 *
 * URL: /embed/chat?channelId=<channelId>  (токен — через postMessage, см. ниже)
 *
 * - НЕ рендерит сайдбар, навигацию, друзей, settings, stories.
 * - Только окно одного канала «Общий» СНТ + composer.
 * - История messsages с бесконечным скроллом назад.
 * - Auth (#2-full): родитель (russnt.ru) шлёт access-token в iframe через
 *   postMessage {type:'swipa-embed-auth', token} после нашего сигнала
 *   {type:'swipa-embed-ready'}. Токена в URL нет → не оседает в access-логах.
 *   Legacy `?token=` в URL ещё принимается (fallback на время раскатки russnt).
 *
 * Если токен не приходит (ни URL, ни postMessage) — заглушка «доступ закрыт».
 */
export default function EmbedChatPage() {
  const [searchParams] = useSearchParams();
  // SECURITY (#2-full, 2026-06-14): токен приходит от russnt.ru через postMessage,
  // а НЕ в URL (URL-токен оседал в access-логах nginx swipa.ru + history/referer).
  // `?token=` оставлен как legacy-fallback на время раскатки russnt-стороны.
  const urlToken = searchParams.get('token');
  const channelId = searchParams.get('channelId');
  const [token, setToken] = useState<string | null>(urlToken);

  const setActiveChannel = useChatStore((s) => s.setActiveChannel);
  const channelMessages = useChatStore((s) => s.channelMessages);
  const setChannelMessages = useChatStore((s) => s.setChannelMessages);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  // Подписываемся на server events (message:new, message:edited, etc.) чтобы
  // отправленный стикер/сообщение сразу обновляли local chatStore и UI без
  // задержки до следующего refresh. Безопасно вызывать до connectSocket — hook
  // переподписывается при изменении сокета.
  useSocketEvents();

  const [bootError, setBootError] = useState<string | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  // #2-full: postMessage-handshake с russnt.ru. Если токена нет в URL — объявляем
  // родителю «ready» и принимаем токен ТОЛЬКО от доверенного russnt-origin'а
  // (origin-валидация = защита от чужого встраивателя; CSP frame-ancestors в nginx
  // уже ограничивает, кто вообще может фреймить /embed — это второй слой).
  useEffect(() => {
    if (urlToken) return; // legacy: токен уже пришёл в URL
    const isTrustedParent = (origin: string) =>
      origin === 'https://russnt.ru' || origin.endsWith('.russnt.ru');
    const onMessage = (e: MessageEvent) => {
      if (!isTrustedParent(e.origin)) return;
      const data = e.data;
      if (data && data.type === 'swipa-embed-auth' && typeof data.token === 'string') {
        setToken(data.token);
      }
    };
    window.addEventListener('message', onMessage);
    // «ready» — лишь сигнал без секретов; сам токен валидируем по e.origin выше.
    try { window.parent?.postMessage({ type: 'swipa-embed-ready' }, '*'); } catch { /* noop */ }
    // Не вечный спиннер: если токен не пришёл за 8с — показываем ошибку.
    const timeout = setTimeout(() => {
      setToken((cur) => {
        if (!cur) { setBootError('Нет токена доступа'); setBootLoading(false); }
        return cur;
      });
    }, 8000);
    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timeout);
    };
  }, [urlToken]);

  // Bootstrap auth — токен из URL (legacy) или postMessage.
  useEffect(() => {
    // Токена ещё нет (ждём postMessage от russnt) — это не ошибка; таймаут выше
    // покажет ошибку, если токен так и не придёт.
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        // Использую setAccessToken (он же localStorage.setItem) + me() —
        // не вызываю setSession чтобы не плодить push-permission popup в iframe.
        setAccessToken(token);
        // LEGACY-путь (?token= в URL): сразу чистим token из URL после потребления
        // (history.replaceState), чтобы он не оседал в history/referer. Основной
        // путь теперь — postMessage (см. effect выше), там токена в URL нет вовсе.
        // Этот блок безвреден, когда токен пришёл по postMessage (тогда ?token=
        // в URL отсутствует и delete — no-op).
        try {
          const cleanUrl = new URL(window.location.href);
          if (cleanUrl.searchParams.has('token')) {
            cleanUrl.searchParams.delete('token');
            window.history.replaceState(
              window.history.state,
              '',
              cleanUrl.pathname + cleanUrl.search + cleanUrl.hash,
            );
          }
        } catch {
          /* URL API недоступен — некритично */
        }
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me);
        useAuthStore.setState({ isAuthenticated: true });
        connectSocket(token);
        setBootLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setBootError(e?.response?.data?.message || 'Не удалось войти по токену');
        setBootLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, setAccessToken, setUser]);

  // Set active channel
  useEffect(() => {
    if (channelId && isAuthenticated) {
      setActiveChannel(channelId);
      return () => setActiveChannel(null);
    }
  }, [channelId, isAuthenticated, setActiveChannel]);

  // Load channel meta
  const { data: channel } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => channelsApi.get(channelId!),
    enabled: !!channelId && isAuthenticated,
  });

  // Load message pages (infinite scroll backwards)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['messages', 'channel', channelId],
    queryFn: ({ pageParam }) =>
      messagesApi.getChannelMessages(channelId!, { cursor: pageParam as string, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!channelId && isAuthenticated,
  });

  // Sync into chatStore so socket events update view in real-time
  useEffect(() => {
    if (!data || !channelId) return;
    const allMsgs = data.pages.flatMap((p) => p.data).reverse();
    setChannelMessages(channelId, allMsgs);
  }, [data, channelId, setChannelMessages]);

  const messages = useMemo(() => {
    return channelId ? channelMessages[channelId] ?? [] : [];
  }, [channelId, channelMessages]);

  // ── UI ────────────────────────────────────────────────────────────────────
  if (bootLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-950 text-surface-400">
        <div className="text-sm">Подключение к чату…</div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-surface-950 text-surface-200 px-4 text-center">
        <Lock size={32} className="text-surface-500 mb-3" />
        <div className="font-medium">Доступ к чату закрыт</div>
        <div className="text-xs text-surface-500 mt-1">{bootError}</div>
      </div>
    );
  }

  if (!channelId) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-950 text-surface-400">
        <div className="text-sm">Не указан канал</div>
      </div>
    );
  }

  return (
    // fixed inset-0 (вместо h-screen): в iframe 100vh = viewport iframe'а,
    // который у некоторых хостов (русnt) может быть 0 при auto-height. fixed
    // inset-0 гарантированно занимает доступную область embedded-окна.
    <div className="fixed inset-0 flex flex-col bg-surface-950 text-surface-100">
      {/* Минимальный header — только название СНТ + аватар */}
      <div className="h-12 border-b border-surface-800 px-3 flex items-center gap-3 flex-shrink-0 bg-surface-900">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {channel?.displayName || 'Общий'}
          </div>
          {channel?._count?.members != null && (
            <div className="text-[11px] text-surface-500">
              {channel._count.members} участн{channel._count.members === 1 ? 'ик' : 'иков'}
            </div>
          )}
        </div>
        {user && (
          <div className="flex items-center gap-2 text-xs text-surface-400">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="w-7 h-7 rounded-full object-cover"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-surface-700 flex items-center justify-center text-[11px] font-medium">
                {(user.displayName || user.username || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      {/* MessageList корневой div = `flex-1 flex flex-col min-h-0`, поэтому
          обёртка обязана быть flex-контейнером с `min-h-0` — иначе flex-1
          ни на что не растянется и внутренний overflow-y-auto не будет
          иметь высоты, и скролл не работает.
          touch-action гарантирует что мобильный браузер пропустит вертикальный
          скролл даже если parent (русnt) ставит touch-action:none. */}
      <div className="flex-1 min-h-0 flex flex-col" style={{ touchAction: 'pan-y' }}>
        <MessageList
          messages={messages}
          onLoadMore={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          hasMore={hasNextPage}
          loading={isFetchingNextPage}
          onReply={setReplyTo}
          embedMode
        />
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 border-t border-surface-800 bg-surface-900">
        <MessageInput
          channelId={channelId}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          placeholder="Написать в чат СНТ…"
          stackInput
        />
      </div>
    </div>
  );
}
